import { FieldError, FieldErrors } from "react-hook-form";

/**
 * Contact details in a public free-text field, and the form glue that blocks a
 * submit carrying them.
 *
 * Deliberately the rough guard: `Moderations::ModerateTaskDescription` decides
 * what is publishable, so this only has to catch the honest mistake fast enough
 * to save a round trip. Targets the legacy rule, minus its accidents.
 */

export type ContactInfoRule = "phone" | "emailOrUrl";

/** Enough digits to be a phone number rather than a quantity or a measurement. */
const MIN_PHONE_DIGITS = 6;

/** "0475/12/34/56" — digits and punctuation, no spaces. */
const PUNCTUATED_DIGITS = /[\d_.,/-]{9,20}/g;

/**
 * "0475 12 34 56". Commas are not separators here, unlike above, or a list of
 * sizes ("des planches de 2, 4, 6, 8, 10, 12 cm") would read as a phone number.
 */
const SPACED_DIGITS = new RegExp(`(?:[\\s.]*\\d){${MIN_PHONE_DIGITS},}`);

const DIGITS = /\d/g;

const countDigits = (text: string) => (text.match(DIGITS) ?? []).length;

let cachedDates: { year: number; pattern: RegExp } | undefined;

/** Rebuilt once a year, not once a keystroke — this runs on every revalidation. */
const recentDatePattern = () => {
  const year = new Date().getFullYear();

  if (cachedDates?.year !== year) {
    const years = [year - 1, year, year + 1];
    cachedDates = {
      year,
      // The separator repeats via `\2`, and whitespace is one of them: "12 03 2026".
      pattern: new RegExp(
        `\\b([0-2]?\\d|3[01])([\\s/.-])(0?\\d|1[0-2])\\2(${years.join("|")}|${years
          .map((each) => String(each).slice(-2))
          .join("|")})\\b`,
        "g"
      ),
    };
  }

  return cachedDates.pattern;
};

/** Blanked rather than dropped, so digits either side cannot close up into a run. */
const blankOutRecentDates = (text: string) =>
  text.replace(recentDatePattern(), (match) => " ".repeat(match.length));

const containsPhoneNumber = (text: string) => {
  const withoutDates = blankOutRecentDates(text);
  // Counting the digits is the check legacy meant to make: matching the shape
  // alone read a rule of dashes ("----------") as a phone number.
  const runs = withoutDates.match(PUNCTUATED_DIGITS) ?? [];

  return (
    runs.some((run) => countDigits(run) >= MIN_PHONE_DIGITS) ||
    SPACED_DIGITS.test(withoutDates)
  );
};

/** Case-insensitive: none of these collide with ordinary prose. */
const CONTACT_MARKERS = ["@", "www", "http", "dotcom", "come.ac", "/ringtwice"];

/**
 * Case is load-bearing here, not an oversight: ".Net gekocht" is a sentence that
 * lost its space and ".net here" is a domain, and only the capital separates
 * them. The boundary is what keeps ".bedankt" from reading as a Belgian domain.
 * No exemption for known domains, so naming a shop ("ikea.be") is refused too.
 */
const DOMAIN_SUFFIX = /\.(be|nl|com|net|link|click)\b/;

const containsContactMarker = (text: string) => {
  const lowered = text.toLowerCase();

  return (
    CONTACT_MARKERS.some((marker) => lowered.includes(marker)) ||
    DOMAIN_SUFFIX.test(text)
  );
};

/**
 * Legacy's two other checks are dropped: a `p_category` loosening this flow
 * cannot reach, and a last-name lookup on an attribute no page ever rendered.
 */
export const detectContactInfo = (
  text: string
): ContactInfoRule | undefined => {
  if (containsPhoneNumber(text)) return "phone";
  if (containsContactMarker(text)) return "emailOrUrl";

  return undefined;
};

/** The `validate` key, and so the `type` react-hook-form stamps on the error. */
export const CONTACT_INFO_ERROR = "contactInfo";

/**
 * Returns `false` rather than a message: what the user is told lives in
 * `ContactInfoModal`, which the screens open on this error.
 */
export const noContactInfo = (value: unknown) =>
  typeof value !== "string" || detectContactInfo(value) === undefined;

const isFieldError = (value: unknown): value is FieldError =>
  typeof value === "object" && value !== null && "type" in value;

/**
 * Recurses, because a question key carrying a `.` makes react-hook-form nest its
 * error — and a miss here is a dead CTA, the validator having no message to show.
 */
export const hasContactInfoError = (errors: FieldErrors): boolean =>
  Object.values(errors).some((error) =>
    isFieldError(error)
      ? error.type === CONTACT_INFO_ERROR
      : hasContactInfoError((error ?? {}) as FieldErrors)
  );
